/**
 * Decides what an API failure should actually say to the user.
 *
 * Server messages cannot be trusted verbatim. The BFF forwards developer
 * strings such as "CRM request failed: 406", and all three microservices
 * JSON.parse raw Errors, so stack traces and SQL text can reach the response
 * body. func-membership-api additionally embeds transaction IDs and
 * timestamps. Anything that fails the filter below falls back to generic copy.
 */

const MAX_DISPLAY_LENGTH = 200;

const DEVELOPER_MESSAGE_PATTERNS = [
  /request failed/i,
  /failed:\s*\d/i,
  /unexpected error occurred/i,
  /\btransaction id\b/i,
  /\bError:/,
  // Stack frames. Anchored to real frame syntax so ordinary prose such as
  // "not available at this time" is not mistaken for one.
  /\bat\s+\w+(\.\w+)+\s*\(/,
  /\bat\s+\S+:\d+:\d+/,
  /\b(select|insert|update|delete)\b.*\bfrom\b/i,
  /\bconstraint\b|\bdeadlock\b|\bECONN|\bETIMEDOUT\b|\bEHOSTUNREACH\b|\bENOTFOUND\b/i,
  /undefined|null reference|cannot read propert/i,
  // Internal identifiers and infrastructure the user must never see.
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i,
  /\b[0-9a-f]{16,}\b/i,
  /\b\d{8,}\b/,
  /\b[\w-]+\.(internal|local|azurewebsites\.net|database\.windows\.net)\b/i,
  /\b\d{1,3}(\.\d{1,3}){3}\b/,
];

// Statuses where the server is describing a business outcome the user caused
// and can act on, rather than an internal fault.
const BUSINESS_STATUSES = new Set([400, 406, 409, 422]);

export function isDisplayableMessage(message) {
  if (typeof message !== "string") return false;

  const trimmed = message.trim();
  if (!trimmed || trimmed.length > MAX_DISPLAY_LENGTH) return false;
  if (/[\n\r]/.test(trimmed)) return false;
  if (/^[{[]/.test(trimmed)) return false;

  return !DEVELOPER_MESSAGE_PATTERNS.some((pattern) => pattern.test(trimmed));
}

const GENERIC = {
  key: "errorModal.generic",
  fallback: "Something went wrong. Please try again.",
};

const BY_STATUS = {
  session: {
    key: "errorModal.session",
    fallback:
      "Your session has expired. Please refresh the page and sign in again.",
  },
  forbidden: {
    key: "errorModal.forbidden",
    fallback: "You do not have access to the store. Please contact support.",
  },
  productUnavailable: {
    key: "errorModal.productUnavailable",
    fallback: "This item is not available to you.",
  },
  notFound: {
    key: "errorModal.notFound",
    fallback: "We could not find what you were looking for.",
  },
  listItemDuplicate: {
    key: "errorModal.listItemDuplicate",
    fallback: "This variant is already in the list.",
  },
  listItemGone: {
    key: "errorModal.listItemGone",
    fallback: "This item is no longer in the list.",
  },
  network: {
    key: "errorModal.network",
    fallback:
      "We are having trouble connecting. Please check your connection and try again.",
  },
  server: {
    key: "errorModal.server",
    fallback: "Something went wrong on our end. Please try again in a moment.",
  },
};

/**
 * Resolves user-facing copy for an error produced by parseApiError.
 *
 * @param {Error} error - The rejected error.
 * @returns {{ key: string|null, fallback: string, serverText: string|null }}
 *   `serverText` is set only when the server supplied something safe to show;
 *   otherwise the caller should translate `key` with `fallback`.
 */
export function resolveUserMessage(error) {
  const status = Number(error?.status);

  if (!status) {
    // No status means the request never completed: offline, DNS, CORS, abort.
    return { ...BY_STATUS.network, serverText: null };
  }

  // 401 means the token itself is invalid/expired (BFF auth middleware).
  // 403 means the token is valid but access was refused for a business reason
  // (e.g. a comp member is not an active member) — the server's message is
  // the whole point, so show it instead of a generic session prompt.
  if (status === 401) {
    return { ...BY_STATUS.session, serverText: null };
  }

  if (error?.code === "LIST_ITEM_DUPLICATE") {
    return { ...BY_STATUS.listItemDuplicate, serverText: null };
  }

  if (error?.code === "LIST_ITEM_NOT_FOUND") {
    return { ...BY_STATUS.listItemGone, serverText: null };
  }

  if (status === 403) {
    const serverText = isDisplayableMessage(error?.serverMessage)
      ? error.serverMessage.trim()
      : null;

    // A single-product rejection (e.g. addCartItem/updateCartItem visibility
    // guard) is not the same failure as being locked out of the whole store,
    // and the generic "contact support" copy is actively misleading here.
    if (error?.code === "PRODUCT_NOT_AVAILABLE") {
      return { ...BY_STATUS.productUnavailable, serverText };
    }

    return { ...BY_STATUS.forbidden, serverText };
  }

  if (
    BUSINESS_STATUSES.has(status) &&
    isDisplayableMessage(error?.serverMessage)
  ) {
    return { ...GENERIC, serverText: error.serverMessage.trim() };
  }

  if (status === 404) {
    return { ...BY_STATUS.notFound, serverText: null };
  }

  if (status >= 500) {
    return { ...BY_STATUS.server, serverText: null };
  }

  return { ...GENERIC, serverText: null };
}

export const errorModalTitle = {
  key: "errorModal.title",
  fallback: "Something Went Wrong",
};

// The core AddCustomerPost endpoint rethrows the membership 406 card-type
// rejection as a 500, so the status alone cannot identify it.
export function isCardNotAcceptedError(error) {
  const message = error?.serverMessage ?? error?.message;
  return typeof message === "string" && /\bnot accepted\b/i.test(message);
}

export const cardNotAcceptedMessage = {
  key: "errorModal.cardNotAccepted",
  fallback:
    "Card not accepted. Please enter a valid Visa®, Mastercard®, Discover®, or Diners Club International®.",
};
